/**
 * localCache — lightweight localStorage wrapper with TTL and staleness tracking.
 * Used across the app to show cached data immediately while fetching fresh data.
 */

const PREFIX = 'rb_cache_';

interface CacheEntry<T> {
  data: T;
  savedAt: number; // epoch ms
}

/** Write data to the local cache */
export function setCached<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = { data, savedAt: Date.now() };
    localStorage.setItem(PREFIX + key, JSON.stringify(entry));
  } catch {
    // Silently ignore storage errors (quota, private mode)
  }
}

/** Read data from the local cache. Returns null if nothing stored. */
export function getCached<T>(key: string): { data: T; savedAt: Date } | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry<T>;
    return { data: entry.data, savedAt: new Date(entry.savedAt) };
  } catch {
    return null;
  }
}

/** Remove a cached entry */
export function clearCached(key: string): void {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {}
}

/** Returns a human-readable "last updated X ago" string */
export function formatCacheAge(savedAt: Date): string {
  const diffMs = Date.now() - savedAt.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

/** Check if cached data is older than ttlMinutes */
export function isCacheStale(savedAt: Date, ttlMinutes = 5): boolean {
  return Date.now() - savedAt.getTime() > ttlMinutes * 60_000;
}
